import { setSessionLost } from "@/features/auth/authSlice";
import { store } from "@/store/store";
import { ApolloClient, HttpLink, InMemoryCache, NormalizedCacheObject } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { createPersistedQueryLink } from "@apollo/client/link/persisted-queries";
import { CachePersistor, LocalStorageWrapper } from "apollo3-cache-persist";
import { isTokenExpired } from "../utils/isTokenExpiared";

const httpLink = new HttpLink({
  uri: "/api/graphql", // Proxied via Next.js rewrites to avoid CORS
  credentials: "include", // Needed for refresh token cookies
});

// Operations issued by the auth flow itself must not trigger a sign-out on
// failure (e.g. a bad password is a normal Signin error, not a session loss).
const AUTH_BYPASS_OPERATIONS = new Set([
  "Signin",
  "SignUp",
  "RefreshTokens",
  "SignOut",
  "getCurrentUser",
]);

function isUnauthenticatedError(
  graphQLErrors: readonly { extensions?: Record<string, unknown> }[] | undefined,
  networkError: unknown,
): boolean {
  if (
    graphQLErrors?.some((e) => {
      const code = e.extensions?.code;
      return code === "UNAUTHENTICATED" || code === "FORBIDDEN";
    })
  ) {
    return true;
  }
  if (networkError && typeof networkError === "object" && "statusCode" in networkError) {
    const status = (networkError as { statusCode?: number }).statusCode;
    if (status === 401 || status === 403) return true;
  }
  return false;
}

let signingOut = false;
async function handleSessionLost() {
  if (signingOut) return;
  signingOut = true;
  try {
    const { signOutThunk } = await import("@/features/auth/authSlice");
    await store.dispatch(signOutThunk());
  } catch (err) {
    console.error("Failed to dispatch signOut after auth error:", err);
  } finally {
    if (
      typeof window !== "undefined" &&
      !window.location.pathname.startsWith("/auth")
    ) {
      window.location.assign("/auth");
    }
    setTimeout(() => {
      signingOut = false;
    }, 1000);
  }
}

const errorLink = onError(({ operation, graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach((e) => console.error(`[GraphQL error]: ${e.message}`));
  }
  if (networkError) {
    console.error(`[Network error]: ${networkError.message}`);
  }
  const opName = operation.operationName || "";
  if (AUTH_BYPASS_OPERATIONS.has(opName)) return;
  if (isUnauthenticatedError(graphQLErrors, networkError)) {
    // Flip the flag synchronously so ProtectedRoute unmounts the page tree
    // in the same React tick — otherwise the failing query's error UI
    // renders during the gap between this callback and the redirect.
    store.dispatch(setSessionLost());
    void handleSessionLost();
  }
});

let refreshingPromise: Promise<string | null> | null = null;

async function refreshAccessToken(dispatch: typeof store.dispatch) {
  if (!refreshingPromise) {
    refreshingPromise = (async () => {
      try {
        const response = await fetch(
          "/api/graphql",
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              query: `
              mutation RefreshTokens {
                refreshTokens {
                  accessToken
                }
              }
            `,
            }),
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const jsonResponse = await response.json();

        if (jsonResponse?.data?.refreshTokens) {
          const accessToken = jsonResponse.data.refreshTokens.accessToken;

          localStorage.setItem("accessToken", accessToken);

          return accessToken;
        } else {
          throw new Error("Invalid refresh token response.");
        }
      } catch (err) {
        console.error("Error refreshing token:", err);
        const { signOutThunk } = await import("@/features/auth/authSlice");
        // Defer dispatch to avoid calling React state updates inside Apollo's
        // async setContext link (Apollo 3.14 invariant: no side-effects during processing)
        setTimeout(() => dispatch(signOutThunk()), 0);
        return null;
      } finally {
        refreshingPromise = null;
      }
    })();
  }

  return refreshingPromise;
}

const authLink = setContext(async (operation, { headers }) => {
  const operationName = operation.operationName || "";
  const bypassOperations = ["Signin", "SignUp", "RefreshTokens", "SignOut"];

  if (bypassOperations.includes(operationName)) {
    return { headers };
  }

  let accessToken = localStorage.getItem("accessToken");



  if (accessToken && isTokenExpired(accessToken)) {
    console.log("Access token expired, refreshing...");
    const dispatch = store.dispatch;
    accessToken = await refreshAccessToken(dispatch); // wait for new token
  }

  if (!accessToken) {
    console.warn("No access token found");
  }

  return {
    headers: {
      ...headers,
      Authorization: accessToken ? `Bearer ${accessToken}` : "",
    },
  };
});

// SHA-256 via WebCrypto — APQ needs hashes for the persisted-query link.
async function sha256(query: string): Promise<string> {
  const buf = new TextEncoder().encode(query);
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// `crypto.subtle` only exists in a secure context (HTTPS or localhost).
// Over plain HTTP on a LAN IP (e.g. a phone hitting http://192.168.x.x:3004)
// it's undefined, so sha256() would throw on every operation. Skip APQ there
// and send full queries instead — correctness over the CDN-cache optimization.
const apqAvailable =
  typeof window !== "undefined" && !!window.crypto?.subtle;

// Send a short query hash instead of the full query text; falls back to
// a full POST on a cache miss. `useGETForHashedQueries` makes successful
// cache hits GET-able and therefore CDN-cacheable.
const persistedQueryLink = apqAvailable
  ? createPersistedQueryLink({ sha256, useGETForHashedQueries: true })
  : null;

const cache = new InMemoryCache();

// Persist Apollo cache to localStorage so first paint after a hard refresh
// shows last-known data while the network revalidates in the background.
// Auth fields stay in Redux (redux-persist) — never persist tokens here.
//
// The persisted snapshot has a max age: if the user opens the app after
// being away longer than CACHE_MAX_AGE_MS, drop the saved cache and start
// fresh. Without this, `cache-first` queries can resurrect arbitrarily old
// data — e.g. a clock-in record from 4 days ago still appearing as the
// active shift. The on-disk snapshot is stamped on every write via a tiny
// wrapper around LocalStorageWrapper; restore() checks the stamp first.
const CACHE_KEY = "structura.apollo-cache";
const CACHE_TIMESTAMP_KEY = "structura.apollo-cache.updatedAt";
const CACHE_MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes

class TimestampedLocalStorage extends LocalStorageWrapper {
  setItem(key: string, value: string) {
    const result = super.setItem(key, value);
    try {
      window.localStorage.setItem(CACHE_TIMESTAMP_KEY, String(Date.now()));
    } catch {
      // Quota or private-mode failures here are non-fatal — the persistor
      // itself will surface the underlying error on the real write.
    }
    return result;
  }
}

function persistedCacheIsStale(): boolean {
  const stamp = window.localStorage.getItem(CACHE_TIMESTAMP_KEY);
  if (!stamp) return false; // first run, or pre-stamp data — let restore decide
  const written = Number(stamp);
  if (!Number.isFinite(written)) return true;
  return Date.now() - written > CACHE_MAX_AGE_MS;
}

export let apolloPersistor: CachePersistor<NormalizedCacheObject> | null = null;
if (typeof window !== "undefined") {
  const persistor = new CachePersistor({
    cache,
    storage: new TimestampedLocalStorage(window.localStorage),
    maxSize: 1_048_576, // 1MB — past this the persistor pauses writes
    debounce: 500,
    key: CACHE_KEY,
  });
  apolloPersistor = persistor;

  if (persistedCacheIsStale()) {
    // Drop the on-disk snapshot before anything reads from cache. purge()
    // also resets the persistor's internal state so subsequent writes are
    // tracked normally.
    void persistor.purge();
    window.localStorage.removeItem(CACHE_TIMESTAMP_KEY);
  } else {
    // Fire-and-forget restore. `restore()` is synchronous enough on
    // localStorage that queries fired during initial render still find
    // populated cache entries.
    void persistor.restore();
  }
}

const client = new ApolloClient({
  link: persistedQueryLink
    ? errorLink.concat(authLink).concat(persistedQueryLink).concat(httpLink)
    : errorLink.concat(authLink).concat(httpLink),
  cache,
});

export default client;
